import React from "react";
import {
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  MoreVert,
  Visibility,
  Block,
  CheckCircle,
  Delete,
} from "@mui/icons-material";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { IUser } from "../../../../services/user.service";
import { useRole } from "../../../../context/RoleContext";

interface TableRowItemProps {
  item: IUser;
  handleClick: (id: string) => void;
  onBlockToggle: (id: string, isBlocked: boolean) => void;
  onDelete: (id: string) => void;
  activeWindow: 7 | 30 | null;
}

export default function TableRowItem({
  item,
  handleClick,
  onBlockToggle,
  onDelete,
  activeWindow,
}: TableRowItemProps) {
  const { t } = useTranslation();
  const { isSuperAdmin } = useRole();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewClick = () => {
    handleClick(item._id);
    handleMenuClose();
  };

  const handleBlockClick = () => {
    onBlockToggle(item._id, !item.isBlocked);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    onDelete(item._id);
    handleMenuClose();
  };

  const isOnline =
    !!item.lastSeenAt && Date.now() - new Date(item.lastSeenAt).getTime() < 2 * 60 * 1000;
  const lastSeenMs = item.lastSeenAt ? new Date(item.lastSeenAt).getTime() : 0;
  const lastLoginMs = item.lastLoginAt ? new Date(item.lastLoginAt).getTime() : 0;
  const lastActivity = Math.max(lastSeenMs, lastLoginMs);
  const isActive = activeWindow === null || lastActivity >= Date.now() - activeWindow * 24 * 60 * 60 * 1000;
  const statusLabel = isOnline ? t("status.online") : isActive ? t("status.active") : t("status.inactive");
  const statusColor: "success" | "default" = isOnline || isActive ? "success" : "default";

  return (
    <>
      <TableRow
        hover
        onClick={() => handleClick(item._id)}
        sx={{ cursor: "pointer" }}
      >
        <TableCell>{item.firstName || t("common.notAvailable")}</TableCell>
        <TableCell>{item.lastName || t("common.notAvailable")}</TableCell>
        <TableCell>
          {item.country_code} {item.phone}
        </TableCell>
        <TableCell>{item.email || t("common.notAvailable")}</TableCell>
        <TableCell>
          {item.gender ? (
            <Chip
              label={
                item.gender === "Male"
                  ? t("users.genderMale")
                  : item.gender === "Female"
                    ? t("users.genderFemale")
                    : item.gender
              }
              size="small"
              color={item.gender === "Male" ? "primary" : item.gender === "Female" ? "secondary" : "default"}
              variant="outlined"
            />
          ) : (
            t("common.notAvailable")
          )}
        </TableCell>
        <TableCell>
          <Chip
            label={
              isOnline ? (
                <span>
                  <span
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "#2e7d32",
                      marginRight: 6,
                      verticalAlign: "middle",
                    }}
                  />
                  {statusLabel}
                </span>
              ) : (
                statusLabel
              )
            }
            color={statusColor}
            size="small"
            sx={isOnline ? { "& .MuiChip-label": { fontWeight: 600 } } : undefined}
          />
        </TableCell>
        <TableCell>
          {item.createdAt
            ? format(new Date(item.createdAt), "MMM dd, yyyy")
            : t("common.notAvailable")}
        </TableCell>
        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
          <IconButton
            size="small"
            onClick={handleMenuClick}
            aria-label={t("users.moreActions")}
          >
            <MoreVert />
          </IconButton>
        </TableCell>
      </TableRow>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleViewClick}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("common.viewDetails")}</ListItemText>
        </MenuItem>

        {isSuperAdmin && (
          <MenuItem onClick={handleBlockClick}>
            <ListItemIcon>
              {item.isBlocked ? (
                <CheckCircle fontSize="small" color="success" />
              ) : (
                <Block fontSize="small" color="error" />
              )}
            </ListItemIcon>
            <ListItemText>
              {item.isBlocked ? t("userDetail.unblock") : t("userDetail.block")}
            </ListItemText>
          </MenuItem>
        )}

        <MenuItem onClick={handleDeleteClick}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t("common.delete")}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
